import {
  Button,
  Icon,
  Loader,
  Modal,
  ModalProps,
  modalHoc,
  notificationsManager,
  useModal,
} from '@innowise-group/mui-kit';
import React, { useRef, useState } from 'react';
import * as Styled from './offer-creation-modal.styles';
import { useTranslation } from 'react-i18next';
import {
  CandidateOfferItem,
  FileIdResponse,
  getCandidateByIdSilentThunk,
  useAppDispatch,
  useCandidateFilesAPI,
  useCandidateOffersAPI,
  useCurrentCandidate,
  useOptionsApi,
  useUnitsAdministrationOptions,
  useValidationSchemaOffer,
} from '@innowise-group/core';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { GeneratedOfferDocument, OfferForm, defaultOfferFormValues } from '@shared-components';
import { pdf } from '@react-pdf/renderer';
import { FilePreviewModal } from '@innowise-group/modals';
import { format } from 'date-fns';
import { DateFormats, OfferFilesAcceptFormats, OfferFilesFormats, MaxFileSize } from '@constants';

const OfferCreationModal: React.FC<ModalProps> = ({ close }) => {
  const { t } = useTranslation();
  const { validationSchema } = useValidationSchemaOffer();
  const { createFileId, isFilesIdsLoading } = useCandidateFilesAPI();
  const { createOffer, isFormLoading } = useCandidateOffersAPI();
  const unitsOptions = useUnitsAdministrationOptions();
  const { officeOptions, countriesOptions, locationOptions, jobTitleOptions } = useOptionsApi();
  const dispatch = useAppDispatch();

  const currentCandidate = useCurrentCandidate();
  const [showFilePreview] = useModal(FilePreviewModal);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<FileIdResponse>(null);

  const methods = useForm<CandidateOfferItem>({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: defaultOfferFormValues,
  });

  const { handleSubmit, setValue, watch, getValues, trigger } = methods;
  const offerType = watch('offerType');
  const offerDate = watch('offerDate');

  const fileName = `${offerType}_${currentCandidate.firstNameEn}_${currentCandidate.lastNameEn}_${format(
    new Date(offerDate),
    DateFormats.DayFirst,
  )}.pdf`;

  const onSubmit = async (data: CandidateOfferItem) => {
    await createOffer({ ...data, candidateId: currentCandidate.id });

    close();
    await dispatch(getCandidateByIdSilentThunk(currentCandidate.id));
  };

  const checkValidation = async (event?: React.MouseEvent<HTMLButtonElement>) => {
    trigger();
    return validationSchema
      .validate(getValues(), { abortEarly: false, recursive: true })
      .then(() => {
        return true;
      })
      .catch((err) => {
        const errorsArrays = err.inner?.filter((item) => !item.path.includes('fileId')) || [];
        if (errorsArrays.length) {
          event?.preventDefault();
          event?.stopPropagation();
        }
        return !errorsArrays.length;
      });
  };

  const openPreviewFile = (file: FileIdResponse) => {
    showFilePreview({ fileName, id: file.id, insideModal: true });
  };

  const generateFile = async () => {
    const isValid = await checkValidation();
    if (!isValid) return;

    const blob = await pdf(
      <GeneratedOfferDocument
        candidateName={currentCandidate.localizedFirstName}
        {...getValues()}
        unitsOptions={unitsOptions}
        jobTitleOptions={jobTitleOptions}
        officeOptions={officeOptions}
        citiesOptions={locationOptions}
        countryOptions={countriesOptions}
      />,
    ).toBlob();
    const generatedFile = new File([blob], fileName, {
      type: 'application/pdf',
    });
    const file = await createFileId(generatedFile, true, openPreviewFile);
    setValue('fileId', file.id);
    setFile(file);
  };

  const handleDeleteFile = async (event: React.MouseEvent<HTMLOrSVGElement>) => {
    event.stopPropagation();
    if (file) {
      setValue('fileId', null);
      setFile(null);
    }
  };

  const handleOpenFile = () => {
    showFilePreview({
      fileName: file.filename,
      id: file.id,
      insideModal: true,
    });
  };

  const addInputFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileType = event.target.files[0].type || event.target.files[0].name.split('.').reverse()[0];
    if (event.target.files[0] && event.target.files[0].size <= MaxFileSize && OfferFilesFormats.includes(fileType)) {
      try {
        const fileResponse = await createFileId(event.target.files[0], true);
        setFile(fileResponse);
        setValue('fileId', fileResponse.id);
        fileInputRef.current.value = null;
      } catch {
        setFile(null);
        fileInputRef.current.value = null;
      }
    } else {
      notificationsManager.error({
        subtitle: t(
          `notifications.text.${
            !(event.target.files[0]?.size <= MaxFileSize) ? 'invalidFileSize' : 'invalidFileFormat'
          }`,
        ),
      });
      fileInputRef.current.value = null;
      setFile(null);
    }
  };

  return (
    <Modal.Container size="large">
      <Modal.Header onClose={close}>
        <Styled.Header>{t('pages.candidates.offer.offerCreation')}</Styled.Header>
      </Modal.Header>
      <Modal.Body>
        <FormProvider {...methods}>
          {isFormLoading ? <Loader /> : <OfferForm disabled={!!file || isFilesIdsLoading} />}
        </FormProvider>
      </Modal.Body>
      <Styled.Footer>
        {!isFormLoading && !!file && (
          <Styled.FileItem onClick={handleOpenFile}>
            {file.secured && (
              <Styled.SecureFileIconContainer>
                <Icon type="u_lock_alt" />
              </Styled.SecureFileIconContainer>
            )}
            <span>{file.filename}</span>
            <Styled.FileButtonContainer>
              <Icon type="u_plus" onClick={handleDeleteFile} />
            </Styled.FileButtonContainer>
          </Styled.FileItem>
        )}
        {!file && (
          <React.Fragment>
            <Styled.GrayButton
              variant="outlined"
              onClick={checkValidation}
              disabled={isFormLoading}
              startIcon={<Icon type="u_export" size={12} viewBox="3 3 20 20" />}
            >
              {t('buttons.uploadProposal')}
              <Styled.FileInput
                type="file"
                onChange={addInputFile}
                ref={fileInputRef}
                accept={OfferFilesAcceptFormats.join(', ')}
                disabled={isFormLoading}
              />
            </Styled.GrayButton>
            <Styled.FileButton
              variant="outlined"
              onClick={generateFile}
              disabled={isFilesIdsLoading}
              startIcon={<Icon type="u_plus" size={12} viewBox="2 2 18 18" />}
            >
              {t('buttons.makeOffer')}
            </Styled.FileButton>
          </React.Fragment>
        )}
        <Styled.GrayButton onClick={close} variant="outlined" disabled={isFormLoading}>
          {t('buttons.cancel')}
        </Styled.GrayButton>
        {!!file && (
          <Button onClick={handleSubmit(onSubmit)} disabled={isFormLoading}>
            {t('buttons.save')}
          </Button>
        )}
      </Styled.Footer>
    </Modal.Container>
  );
};

export default modalHoc(OfferCreationModal, 'offer-creation-modal');
